import React, { useState, useEffect, useContext } from "react";
import {
  Calculator,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Shield,
  AlertCircle,
  CheckCircle,
  Upload,
  Download,
  FileSpreadsheet,
  User,
  Users
} from "lucide-react";
import * as XLSX from 'xlsx';
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";

// TRANSLATION TEXTS - النصوص المترجمة
// ===============================================
const translations = {
  en: {
    salaryCalculator: "Salary Calculator",
    individualCalculator: "Individual Calculator",
    individualDesc: "Calculate salary for one employee at a time",
    bulkExcelProcessor: "Bulk Excel Processor",
    bulkDesc: "Process multiple employees from Excel files",
    selectCalculationType: "Select Calculation Type",
    calculateNetFromGross: "Calculate Net from Gross",
    netFromGrossDesc: "Enter gross salary and insurance salary",
    netFromGrossExcelDesc: "Upload: Employee Name, Insurance Salary, Gross Salary",
    calculateGrossFromNet: "Calculate Gross from Net",
    grossFromNetDesc: "Enter net salary and insurance salary",
    grossFromNetExcelDesc: "Upload: Employee Name, Insurance Salary, Net Salary",
    insuranceSalary: "Insurance Salary",
    insuranceSalaryRequired: "Insurance Salary (Required)",
    insuranceSalaryError: "Insurance salary is required",
    grossSalary: "Gross Salary",
    grossSalaryError: "Gross salary is required",
    netSalary: "Net Salary",
    netSalaryError: "Net salary is required",
    calculateNow: "Calculate Now",
    reset: "Reset",
    calculationResults: "Calculation Results",
    monthlyTax: "Monthly Tax",
    martyrsFund: "Martyrs Fund (0.0005%)",
    deductionDetails: "Deduction Details",
    employeeInsuranceShare: "Employee Insurance Share (11%)",
    monthlyIncomeTax: "Monthly Income Tax",
    yearlyTax: "Yearly Tax",
    totalDeductions: "Total Deductions",
    note: "Note",
    noteText: "These calculations are based on Egyptian tax law. The annual exemption is 20,000 EGP, and progressive tax rates apply from 10% to 27.5% based on income brackets.",
    uploadExcelFile: "Upload Excel File",
    clickToUpload: "Click to upload or drag and drop",
    excelFilesOnly: "Excel files (.xlsx, .xls) only",
    processExcel: "Process Excel",
    processing: "Processing...",
    downloadResults: "Download Results",
    successfullyProcessed: "Successfully processed",
    employeeRecords: "employee records!",
    resultsPreview: "Results Preview",
    records: "records",
    employeeName: "Employee Name",
    showingFirst: "Showing first 10 of",
    recordsDownload: "records. Download to see all.",
    egp: "EGP",
    enterGrossSalary: "Enter gross salary",
    enterNetSalary: "Enter net salary",
    enterInsuranceSalary: "Enter insurance salary",
    pleaseUploadFile: "Please upload a file first",
    excelFileEmpty: "Excel file is empty",
    invalidDataInRow: "Invalid data in row",
    noValidData: "No valid data to process",
    errorProcessingFile: "Error processing file",
  },
  ar: {
    salaryCalculator: "حاسبة الرواتب",
    individualCalculator: "حاسبة فردية",
    individualDesc: "احسب راتب موظف واحد في كل مرة",
    bulkExcelProcessor: "معالجة إكسل جماعية",
    bulkDesc: "معالجة رواتب عدة موظفين من ملفات إكسل",
    selectCalculationType: "اختر نوع الحساب",
    calculateNetFromGross: "احسب الصافي من الإجمالي",
    netFromGrossDesc: "أدخل الراتب الإجمالي وراتب التأمينات",
    netFromGrossExcelDesc: "تحميل: اسم الموظف، راتب التأمينات، الراتب الإجمالي",
    calculateGrossFromNet: "احسب الإجمالي من الصافي",
    grossFromNetDesc: "أدخل الراتب الصافي وراتب التأمينات",
    grossFromNetExcelDesc: "تحميل: اسم الموظف، راتب التأمينات، الراتب الصافي",
    insuranceSalary: "راتب التأمينات",
    insuranceSalaryRequired: "راتب التأمينات (مطلوب)",
    insuranceSalaryError: "راتب التأمينات مطلوب",
    grossSalary: "الراتب الإجمالي",
    grossSalaryError: "الراتب الإجمالي مطلوب",
    netSalary: "الراتب الصافي",
    netSalaryError: "الراتب الصافي مطلوب",
    calculateNow: "احسب الآن",
    reset: "إعادة تعيين",
    calculationResults: "نتائج الحساب",
    monthlyTax: "الضريبة الشهرية",
    martyrsFund: "صندوق الشهداء (0.0005%)",
    deductionDetails: "تفاصيل الخصومات",
    employeeInsuranceShare: "حصة الموظف في التأمينات (11%)",
    monthlyIncomeTax: "ضريبة الدخل الشهرية",
    yearlyTax: "الضريبة السنوية",
    totalDeductions: "إجمالي الخصومات",
    note: "ملاحظة",
    noteText: "هذه الحسابات مبنية على قانون الضرائب المصري. الإعفاء السنوي هو 20,000 جنيه، وتطبق معدلات ضريبية تصاعدية من 10% إلى 27.5% حسب شرائح الدخل.",
    uploadExcelFile: "تحميل ملف إكسل",
    clickToUpload: "انقر للتحميل أو اسحب وأفلت",
    excelFilesOnly: "ملفات إكسل فقط (.xlsx, .xls)",
    processExcel: "معالجة الإكسل",
    processing: "جاري المعالجة...",
    downloadResults: "تحميل النتائج",
    successfullyProcessed: "تمت معالجة",
    employeeRecords: "سجل موظف بنجاح!",
    resultsPreview: "معاينة النتائج",
    records: "سجل",
    employeeName: "اسم الموظف",
    showingFirst: "عرض أول 10 من",
    recordsDownload: "سجل. قم بالتحميل لرؤية الكل.",
    egp: "ج.م",
    enterGrossSalary: "أدخل الراتب الإجمالي",
    enterNetSalary: "أدخل الراتب الصافي",
    enterInsuranceSalary: "أدخل راتب التأمينات",
    pleaseUploadFile: "الرجاء تحميل ملف أولاً",
    excelFileEmpty: "ملف الإكسل فارغ",
    invalidDataInRow: "بيانات غير صحيحة في الصف",
    noValidData: "لا توجد بيانات صحيحة للمعالجة",
    errorProcessingFile: "خطأ في معالجة الملف",
  }
};

const getLookupValue = (hassan) => {
  if (hassan > 600000 && hassan <= 700000) return 4000;
  else if (hassan > 700000 && hassan <= 800000) return 6750;
  else if (hassan > 800000 && hassan <= 900000) return 10250;
  else if (hassan > 900000) return 15250;
  else return 0;
};

export const calculateNetFromGross = (gross, inssalary) => {
  const empShare = inssalary * 0.11;
  const netSalaryBeforeTaxes = gross - empShare;
  const annualSalaryBefore = netSalaryBeforeTaxes * 12;

  const annualSubmittedSalaryAfterTaxes = annualSalaryBefore <= 20000
    ? 0
    : annualSalaryBefore - 20000;

  let annualTaxes = 0;
  if (annualSubmittedSalaryAfterTaxes <= 40000) {
    annualTaxes = 0;
  } else if (annualSubmittedSalaryAfterTaxes <= 55000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 40000) * 0.10;
  } else if (annualSubmittedSalaryAfterTaxes <= 70000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 55000) * 0.15 + 1500;
  } else if (annualSubmittedSalaryAfterTaxes <= 200000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 70000) * 0.20 + 1500 + 2250;
  } else if (annualSubmittedSalaryAfterTaxes <= 400000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 200000) * 0.225 + 1500 + 2250 + 26000;
  } else if (annualSubmittedSalaryAfterTaxes <= 600000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 400000) * 0.25 + 1500 + 2250 + 26000 + 45000;
  } else if (annualSubmittedSalaryAfterTaxes > 600000 && annualSubmittedSalaryAfterTaxes <= 1200000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 400000) * 0.25 + 1500 + 2250 + 26000 + 45000 + getLookupValue(annualSubmittedSalaryAfterTaxes);
  } else if (annualSubmittedSalaryAfterTaxes > 1200000) {
    annualTaxes = (annualSubmittedSalaryAfterTaxes - 1200000) * 0.275 + 300000;
  }

  const monthlySalaryTax = annualTaxes / 12;
  const martyrsFund = gross * 0.0005;
  const netSalary = netSalaryBeforeTaxes - (monthlySalaryTax + martyrsFund);

  return {
    netSalary,
    monthlyTax: monthlySalaryTax,
    yearlyTax: annualTaxes,
    martyrsFund,
    empShare
  };
};

export const calculateGrossFromNet = (targetNet, insSalary) => {
  let low = targetNet;
  let high = targetNet * 3;
  const tolerance = 0.0001;
  const maxIterations = 200;

  for (let i = 0; i < maxIterations; i++) {
    const mid = (low + high) / 2;
    const { netSalary } = calculateNetFromGross(mid, insSalary);
    const difference = netSalary - targetNet;

    if (Math.abs(difference) < tolerance) {
      return mid;
    }

    if (netSalary < targetNet) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return (low + high) / 2;
};

const UnifiedSalaryCalculator = () => {
  const { theme, currentTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const isRTL = language === "ar";

  const [processingMode, setProcessingMode] = useState("individual");
  const [calculationMode, setCalculationMode] = useState("netFromGross");

  const [inputs, setInputs] = useState({
    gross: "",
    net: "",
    insurance: ""
  });
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});

  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processedData, setProcessedData] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    isMobile: window.innerWidth <= 768,
    isTablet: window.innerWidth > 768 && window.innerWidth <= 1024,
    isDesktop: window.innerWidth > 1024,
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isMobile: width <= 768,
        isTablet: width > 768 && width <= 1024,
        isDesktop: width > 1024,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const validateInputs = () => {
    const newErrors = {};

    if (!inputs.insurance || parseFloat(inputs.insurance) <= 0) {
      newErrors.insurance = t.insuranceSalaryError;
    }

    if (calculationMode === "netFromGross") {
      if (!inputs.gross || parseFloat(inputs.gross) <= 0) {
        newErrors.gross = t.grossSalaryError;
      }
    } else {
      if (!inputs.net || parseFloat(inputs.net) <= 0) {
        newErrors.net = t.netSalaryError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCalculate = () => {
    if (!validateInputs()) return;

    const insurance = parseFloat(inputs.insurance);

    if (calculationMode === "netFromGross") {
      const gross = parseFloat(inputs.gross);
      const result = calculateNetFromGross(gross, insurance);
      setResults({
        ...result,
        grossSalary: gross
      });
    } else {
      const net = parseFloat(inputs.net);
      const grossSalary = calculateGrossFromNet(net, insurance);
      const result = calculateNetFromGross(grossSalary, insurance);
      setResults({
        ...result,
        grossSalary
      });
    }
  };

  const handleReset = () => {
    setInputs({ gross: "", net: "", insurance: "" });
    setResults(null);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      setBulkError(null);
      setSuccess(false);
      setProcessedData(null);
    }
  };

  const processExcel = async () => {
    if (!file) {
      setBulkError(t.pleaseUploadFile);
      return;
    }

    setProcessing(true);
    setBulkError(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        throw new Error(t.excelFileEmpty);
      }

      const processedRows = jsonData.map((row, index) => {
        try {
          const employeeName = row['Employee Name'] || row['employee name'] || row['EmployeeName'] || row['اسم الموظف'] || '';
          const insuranceSalary = parseFloat(row['Insurance Salary'] || row['insurance salary'] || row['InsuranceSalary'] || row['راتب التأمينات'] || 0);

          if (calculationMode === "netFromGross") {
            const grossSalary = parseFloat(row['Gross Salary'] || row['gross salary'] || row['GrossSalary'] || row['الراتب الإجمالي'] || 0);

            if (!grossSalary || !insuranceSalary) {
              throw new Error(`${t.invalidDataInRow} ${index + 2}`);
            }

            const { netSalary, monthlyTax, yearlyTax, martyrsFund } = calculateNetFromGross(grossSalary, insuranceSalary);

            return {
              [t.employeeName]: employeeName,
              [t.insuranceSalary]: insuranceSalary.toFixed(2),
              [t.netSalary]: netSalary.toFixed(2),
              [t.grossSalary]: grossSalary.toFixed(2),
              [t.monthlyTax]: monthlyTax.toFixed(2),
              [t.yearlyTax]: yearlyTax.toFixed(2),
              [t.martyrsFund]: martyrsFund.toFixed(2)
            };
          } else {
            const netSalary = parseFloat(row['Net Salary'] || row['net salary'] || row['NetSalary'] || row['الراتب الصافي'] || 0);

            if (!netSalary || !insuranceSalary) {
              throw new Error(`${t.invalidDataInRow} ${index + 2}`);
            }

            const grossSalary = calculateGrossFromNet(netSalary, insuranceSalary);
            const { netSalary: calculatedNet, monthlyTax, yearlyTax, martyrsFund } = calculateNetFromGross(grossSalary, insuranceSalary);

            return {
              [t.employeeName]: employeeName,
              [t.insuranceSalary]: insuranceSalary.toFixed(2),
              [t.netSalary]: calculatedNet.toFixed(2),
              [t.grossSalary]: grossSalary.toFixed(2),
              [t.monthlyTax]: monthlyTax.toFixed(2),
              [t.yearlyTax]: yearlyTax.toFixed(2),
              [t.martyrsFund]: martyrsFund.toFixed(2)
            };
          }
        } catch (err) {
          console.error(`Error processing row ${index + 2}:`, err);
          return null;
        }
      }).filter(row => row !== null);

      if (processedRows.length === 0) {
        throw new Error(t.noValidData);
      }

      setProcessedData(processedRows);
      setSuccess(true);
      setProcessing(false);
    } catch (err) {
      setBulkError(err.message || t.errorProcessingFile);
      setProcessing(false);
    }
  };

  const downloadExcel = () => {
    if (!processedData) return;

    const worksheet = XLSX.utils.json_to_sheet(processedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, language === 'ar' ? "الرواتب المعالجة" : "Processed Salaries");

    const fileName = `Processed_Salaries_${calculationMode}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const styles = {
    container: {
      marginTop: '-40px',
      direction: isRTL ? 'rtl' : 'ltr',
    },

    card: {
      background: theme === "dark"
        ? "rgba(255,255,255,0)"
        : "#ffffff",
      borderRadius: "20px",
      padding: screenSize.isMobile ? "20px" : "32px",
      marginBottom: "24px",
      boxShadow: "none",
      backdropFilter: "none",
      WebkitBackdropFilter: "none",
      border: theme === "dark"
        ? "1px solid rgba(255,255,255,0.15)"
        : "1px solid rgba(0,0,0,0.05)",
      transition: "all 0.3s ease",
      cursor: "pointer",
      direction: isRTL ? 'rtl' : 'ltr',
    },

    title: {
      fontSize: "27px",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      textAlign: isRTL ? 'right' : 'left',
    },

    subtitle: {
      fontSize: screenSize.isMobile ? "14px" : "16px",
      color: currentTheme.colors.textSecondary,
      textAlign: isRTL ? 'right' : 'left',
    },

    modeSwitch: {
      display: "grid",
      gridTemplateColumns: screenSize.isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
      marginBottom: "32px"
    },

    modeSwitchButton: (active) => ({
      padding: "24px",
      borderRadius: "16px",
      border: `3px solid ${active ? "#2e7cba" : "transparent"}`,
      background: active
        ? "linear-gradient(135deg, rgba(59,130,246,0.15), rgba(139,92,246,0.15))"
        : currentTheme.colors.inputBackground,
      cursor: "pointer",
      transition: "all 0.3s ease",
      textAlign: isRTL ? 'right' : 'left',
    }),

    modeSwitchTitle: {
      fontSize: screenSize.isMobile ? "18px" : "20px",
      fontWeight: "800",
      color: currentTheme.colors.textPrimary,
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-start" : "flex-start",
      gap: "10px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    modeSwitchDesc: {
      fontSize: "14px",
      color: currentTheme.colors.textMuted,
      textAlign: isRTL ? 'right' : 'left',
    },

    calcModeToggle: {
      display: "grid",
      gridTemplateColumns: screenSize.isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
      marginBottom: "32px"
    },

    calcModeButton: (active) => ({
      padding: "20px",
      borderRadius: "16px",
      border: `2px solid ${active ? "#2e7cba" : "transparent"}`,
      background: active
        ? (theme === "dark" ? "rgba(59,130,246,0.2)" : "rgba(59,130,246,0.1)")
        : currentTheme.colors.inputBackground,
      cursor: "pointer",
      transition: "all 0.3s ease",
      textAlign: isRTL ? 'right' : 'left',
    }),

    calcModeTitle: {
      fontSize: screenSize.isMobile ? "16px" : "18px",
      fontWeight: "700",
      color: currentTheme.colors.textPrimary,
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      justifyContent: isRTL ? "flex-start" : "flex-start",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
    },

    calcModeDesc: {
      fontSize: "13px",
      color: currentTheme.colors.textMuted,
      textAlign: isRTL ? 'right' : 'left',
    },

    inputGroup: {
      marginBottom: "24px"
    },

    label: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "15px",
      fontWeight: "600",
      color: currentTheme.colors.textPrimary,
      marginBottom: "12px",
      flexDirection: isRTL ? 'row' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    },

    input: (hasError) => ({
      width: "100%",
      padding: "16px 20px",
      borderRadius: "12px",
      border: hasError
        ? "2px solid #ef4444"
        : `2px solid ${currentTheme.colors.border}`,
      background: currentTheme.colors.inputBackground,
      color: currentTheme.colors.textPrimary,
      fontSize: "16px",
      fontWeight: "600",
      outline: "none",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
      textAlign: isRTL ? 'right' : 'left',
      direction: isRTL ? 'rtl' : 'ltr',
    }),

    error: {
      color: "#ef4444",
      fontSize: "13px",
      marginTop: "8px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      flexDirection: isRTL ? 'row' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    },

    buttonGroup: {
      display: "grid",
      gridTemplateColumns: screenSize.isMobile ? "1fr" : "2fr 1fr",
      gap: "12px",
      marginTop: "32px"
    },

    button: (variant = "primary") => ({
      padding: "16px 24px",
      borderRadius: "12px",
      border: "none",
      fontSize: "16px",
      fontWeight: "700",
      cursor: processing ? "not-allowed" : "pointer",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      opacity: processing ? 0.6 : 1,
      background: variant === "primary"
        ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
        : variant === "success"
          ? "linear-gradient(135deg, #10b981, #059669)"
          : currentTheme.colors.inputBackground,
      color: variant === "primary" || variant === "success" ? "#fff" : currentTheme.colors.textPrimary,
      // boxShadow: variant === "primary" || variant === "success"
      //   ? "0 10px 30px rgba(59,130,246,0.4)"
      //   : "none",
      flexDirection: isRTL ? 'row' : 'row',
    }),

    uploadArea: {
      border: `2px dashed ${currentTheme.colors.border}`,
      borderRadius: "12px",
      padding: screenSize.isMobile ? "32px 16px" : "48px 24px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.3s ease",
      background: currentTheme.colors.inputBackground,
    },

    uploadIcon: {
      margin: "0 auto 16px",
      color: currentTheme.colors.textMuted,
    },

    uploadText: {
      fontSize: screenSize.isMobile ? "14px" : "16px",
      color: currentTheme.colors.textSecondary,
      marginBottom: "8px",
    },

    uploadHint: {
      fontSize: screenSize.isMobile ? "12px" : "14px",
      color: currentTheme.colors.textMuted,
    },

    fileName: {
      marginTop: "12px",
      padding: "12px",
      background: "rgba(16,185,129,0.1)",
      borderRadius: "8px",
      color: "#10b981",
      fontSize: screenSize.isMobile ? "13px" : "14px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      justifyContent: "center",
      flexDirection: isRTL ? 'row' : 'row',
    },

    alertBox: (type) => ({
      padding: screenSize.isMobile ? "12px" : "16px",
      borderRadius: "8px",
      marginTop: "16px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      background: type === "error"
        ? "rgba(239,68,68,0.1)"
        : "rgba(16,185,129,0.1)",
      border: `1px solid ${type === "error" ? "rgba(239,68,68,0.3)" : "rgba(16,185,129,0.3)"}`,
      color: type === "error" ? "#ef4444" : "#10b981",
      fontSize: screenSize.isMobile ? "13px" : "14px",
      flexDirection: isRTL ? 'row' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    }),

    resultsGrid: {
      display: "grid",
      gridTemplateColumns: screenSize.isMobile ? "1fr" : "repeat(2, 1fr)",
      gap: "16px",
      marginTop: "24px"
    },

    resultCard: (color) => ({
      padding: "20px",
      borderRadius: "16px",
      background: `linear-gradient(135deg, ${color}10, ${color}05)`,
      border: `2px solid ${color}30`,
      textAlign: isRTL ? 'right' : 'left',
    }),

    resultLabel: {
      fontSize: "13px",
      fontWeight: "600",
      color: currentTheme.colors.textSecondary,
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      textAlign: isRTL ? 'right' : 'left',
    },

    resultValue: (color) => ({
      fontSize: screenSize.isMobile ? "24px" : "28px",
      fontWeight: "800",
      color: color,
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    }),

    summaryCard: {
      padding: "24px",
      borderRadius: "16px",
      background: "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(16,185,129,0.05))",
      border: "2px solid rgba(16,185,129,0.3)",
      marginTop: "16px",
      direction: isRTL ? 'rtl' : 'ltr',
    },

    summaryTitle: {
      fontSize: "18px",
      fontWeight: "700",
      color: "#10b981",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? 'row' : 'row',
      textAlign: isRTL ? 'right' : 'left',
    },

    summaryRow: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderBottom: currentTheme.colors.borderLight,
      flexDirection: isRTL ? 'row' : 'row',
    },

    summaryLabel: {
      fontSize: "14px",
      color: currentTheme.colors.textSecondary,
      textAlign: isRTL ? 'right' : 'left',
    },

    summaryValue: {
      fontSize: "16px",
      fontWeight: "700",
      color: currentTheme.colors.textPrimary,
      textAlign: isRTL ? 'left' : 'right',
    },

    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: screenSize.isMobile ? "12px" : "14px",
      minWidth: "800px",
      direction: isRTL ? 'rtl' : 'ltr',
    },

    th: {
      padding: screenSize.isMobile ? "10px 12px" : "14px 20px",
      textAlign: isRTL ? 'right' : 'left',
      fontWeight: "700",
      background: currentTheme.colors.inputBackground,
      color: currentTheme.colors.textSecondary,
      borderBottom: `2px solid ${currentTheme.colors.border}`,
      fontSize: screenSize.isMobile ? "11px" : "13px",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
    },

    td: {
      padding: screenSize.isMobile ? "10px 12px" : "12px 20px",
      borderBottom: `1px solid ${currentTheme.colors.borderLight}`,
      color: currentTheme.colors.textPrimary,
      fontSize: screenSize.isMobile ? "11px" : "13px",
      textAlign: isRTL ? 'right' : 'left',
    },

    tableWrapper: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      marginTop: "16px",
      direction: isRTL ? 'rtl' : 'ltr',
    }
  };

  const handleMouseEnter = (e) => {
    if (!screenSize.isMobile) {
      e.currentTarget.style.background = theme === "dark"
        ? "rgba(255,255,255,0.04)"
        : "#ffffff";
      e.currentTarget.style.boxShadow = theme === "dark"
        ? "0 12px 40px rgba(0,0,0,0.4)"
        : "0 12px 40px rgba(0,0,0,0.12)";
      e.currentTarget.style.backdropFilter = theme === "dark" ? "blur(15px)" : "none";
      e.currentTarget.style.WebkitBackdropFilter = theme === "dark" ? "blur(15px)" : "none";
      e.currentTarget.style.transform = "translateY(-5px)";
    }
  };

  const handleMouseLeave = (e) => {
    if (!screenSize.isMobile) {
      e.currentTarget.style.background = theme === "dark"
        ? "rgba(255,255,255,0)"
        : "#ffffff";
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.backdropFilter = "none";
      e.currentTarget.style.WebkitBackdropFilter = "none";
      e.currentTarget.style.transform = "translateY(0)";
    }
  };

  return (
    <div style={styles.container}>
      <div
        style={{
          ...styles.card,
          display: "flex",
          gap: "10px",
          alignItems: "center",
          flexDirection: isRTL ? 'row' : 'row',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Calculator size={26} />
        <h1 style={styles.title}>{t.salaryCalculator}</h1>
      </div>

      <div style={styles.card} >
        <div style={styles.modeSwitch}>
          <div
            style={styles.modeSwitchButton(processingMode === "individual")}
            onClick={() => {
              setProcessingMode("individual");
              handleReset();
              setFile(null);
              setProcessedData(null);
            }}
          >
            <div style={styles.modeSwitchTitle}>
              <User size={24} />
              {t.individualCalculator}
            </div>
            <div style={styles.modeSwitchDesc}>
              {t.individualDesc}
            </div>
          </div>

          <div
            style={styles.modeSwitchButton(processingMode === "bulk")}
            onClick={() => {
              setProcessingMode("bulk");
              handleReset();
              setResults(null);
            }}
          >
            <div style={styles.modeSwitchTitle}>
              <Users size={24} />
              {t.bulkExcelProcessor}
            </div>
            <div style={styles.modeSwitchDesc}>
              {t.bulkDesc}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.card}
       //onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
       >
        <h3 style={{
          fontSize: screenSize.isMobile ? "18px" : "20px",
          fontWeight: "700",
          color: currentTheme.colors.textPrimary,
          marginBottom: "20px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexDirection: isRTL ? 'row' : 'row',
          textAlign: isRTL ? 'right' : 'left',
        }}>
          <Calculator size={20} />
          {t.selectCalculationType}
        </h3>
        <div style={styles.calcModeToggle}>
          <div
            style={styles.calcModeButton(calculationMode === "netFromGross")}
            onClick={() => {
              setCalculationMode("netFromGross");
              handleReset();
              setFile(null);
              setProcessedData(null);
              setBulkError(null);
              setSuccess(false);
            }}
          >
            <div style={styles.calcModeTitle}>
              <TrendingDown size={20} />
              {t.calculateNetFromGross}
            </div>
            <div style={styles.calcModeDesc}>
              {processingMode === "individual"
                ? t.netFromGrossDesc
                : t.netFromGrossExcelDesc}
            </div>
          </div>

          <div
            style={styles.calcModeButton(calculationMode === "grossFromNet")}
            onClick={() => {
              setCalculationMode("grossFromNet");
              handleReset();
              setFile(null);
              setProcessedData(null);
              setBulkError(null);
              setSuccess(false);
            }}
          >
            <div style={styles.calcModeTitle}>
              <TrendingUp size={20} />
              {t.calculateGrossFromNet}
            </div>
            <div style={styles.calcModeDesc}>
              {processingMode === "individual"
                ? t.grossFromNetDesc
                : t.grossFromNetExcelDesc}
            </div>
          </div>
        </div>
      </div>

      {processingMode === "individual" && (
        <React.Fragment>
          <div style={styles.card} 
          //</React.Fragment>onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
          >
            <div style={styles.inputGroup}>
              <label style={styles.label}>
                <Shield size={18} />
                {t.insuranceSalaryRequired}
              </label>
              <input
                type="number"
                placeholder={t.enterInsuranceSalary}
                value={inputs.insurance}
                onChange={(e) => handleInputChange("insurance", e.target.value)}
                style={styles.input(errors.insurance)}
              />
              {errors.insurance && (
                <div style={styles.error}>
                  <AlertCircle size={14} />
                  {errors.insurance}
                </div>
              )}
            </div>

            {calculationMode === "netFromGross" ? (
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <DollarSign size={18} />
                  {t.grossSalary}
                </label>
                <input
                  type="number"
                  placeholder={t.enterGrossSalary}
                  value={inputs.gross}
                  onChange={(e) => handleInputChange("gross", e.target.value)}
                  style={styles.input(errors.gross)}
                />
                {errors.gross && (
                  <div style={styles.error}>
                    <AlertCircle size={14} />
                    {errors.gross}
                  </div>
                )}
              </div>
            ) : (
              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  <DollarSign size={18} />
                  {t.netSalary}
                </label>
                <input
                  type="number"
                  placeholder={t.enterNetSalary}
                  value={inputs.net}
                  onChange={(e) => handleInputChange("net", e.target.value)}
                  style={styles.input(errors.net)}
                />
                {errors.net && (
                  <div style={styles.error}>
                    <AlertCircle size={14} />
                    {errors.net}
                  </div>
                )}
              </div>
            )}

            <div style={styles.buttonGroup}>
              <button
                style={styles.button("primary")}
                onClick={handleCalculate}
              >
                <Calculator size={20} />
                {t.calculateNow}
              </button>
              <button
                style={styles.button("secondary")}
                onClick={handleReset}
              >
                <RefreshCw size={20} />
                {t.reset}
              </button>
            </div>
          </div>

          {results && (
            <div style={styles.card} 
           // onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
            >
              <h2 style={{
                fontSize: screenSize.isMobile ? "22px" : "26px",
                fontWeight: "800",
                color: currentTheme.colors.textPrimary,
                marginBottom: "24px",
                textAlign: isRTL ? 'right' : 'center',
              }}>
                {t.calculationResults}
              </h2>

              <div style={styles.resultsGrid}>
                <div style={styles.resultCard("#2e7cba")}>
                  <div style={styles.resultLabel}>{t.grossSalary}</div>
                  <div style={styles.resultValue("#2e7cba")}>
                    <DollarSign size={28} />
                    {results.grossSalary.toFixed(2)} {t.egp}
                  </div>
                </div>

                <div style={styles.resultCard("#10b981")}>
                  <div style={styles.resultLabel}>{t.netSalary}</div>
                  <div style={styles.resultValue("#10b981")}>
                    <DollarSign size={28} />
                    {results.netSalary.toFixed(2)} {t.egp}
                  </div>
                </div>

                <div style={styles.resultCard("#f59e0b")}>
                  <div style={styles.resultLabel}>{t.monthlyTax}</div>
                  <div style={styles.resultValue("#f59e0b")}>
                    {results.monthlyTax.toFixed(2)} {t.egp}
                  </div>
                </div>

                <div style={styles.resultCard("#8b5cf6")}>
                  <div style={styles.resultLabel}>{t.martyrsFund}</div>
                  <div style={styles.resultValue("#8b5cf6")}>
                    {results.martyrsFund.toFixed(3)} {t.egp}
                  </div>
                </div>
              </div>

              <div style={styles.summaryCard}>
                <div style={styles.summaryTitle}>
                  <CheckCircle size={20} />
                  {t.deductionDetails}
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>{t.employeeInsuranceShare}</span>
                  <span style={styles.summaryValue}>{results.empShare.toFixed(2)} {t.egp}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>{t.monthlyIncomeTax}</span>
                  <span style={styles.summaryValue}>{results.monthlyTax.toFixed(2)} {t.egp}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span style={styles.summaryLabel}>{t.yearlyTax}</span>
                  <span style={styles.summaryValue}>{results.yearlyTax.toFixed(2)} {t.egp}</span>
                </div>
                <div style={{
                  ...styles.summaryRow,
                  borderBottom: "none",
                  marginTop: "12px",
                  paddingTop: "16px",
                  borderTop: `2px solid ${currentTheme.colors.border}`
                }}>
                  <span style={{ ...styles.summaryLabel, fontWeight: "700", fontSize: "16px" }}>
                    {t.totalDeductions}
                  </span>
                  <span style={{ ...styles.summaryValue, fontSize: "18px", color: "#ef4444" }}>
                    {(results.empShare + results.monthlyTax + results.martyrsFund).toFixed(2)} {t.egp}
                  </span>
                </div>
              </div>

              <div style={{
                marginTop: "24px",
                padding: "16px",
                borderRadius: "12px",
                background: theme === "dark" ? "rgba(59,130,246,0.1)" : "rgba(59,130,246,0.05)",
                border: "1px solid rgba(59,130,246,0.2)",
                fontSize: "14px",
                color: currentTheme.colors.textSecondary,
                lineHeight: "1.6",
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr',
              }}>
                <strong>{t.note}:</strong> {t.noteText}
              </div>
            </div>
          )}
        </React.Fragment>
      )}

      {processingMode === "bulk" && (
        <React.Fragment>
          <div style={styles.card}
          //</React.Fragment> onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
          >
            <h3 style={{
              fontSize: screenSize.isMobile ? "18px" : "20px",
              fontWeight: "700",
              color: currentTheme.colors.textPrimary,
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isRTL ? 'row' : 'row',
              textAlign: isRTL ? 'right' : 'left',
            }}>
              <Upload size={20} />
              {t.uploadExcelFile}
            </h3>

            <label htmlFor="file-upload">
              <div style={styles.uploadArea}>
                <div style={styles.uploadIcon}>
                  <FileSpreadsheet size={screenSize.isMobile ? 40 : 48} />
                </div>
                <div style={styles.uploadText}>
                  {t.clickToUpload}
                </div>
                <div style={styles.uploadHint}>
                  {t.excelFilesOnly}
                </div>
              </div>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </label>

            {file && (
              <div style={styles.fileName}>
                <CheckCircle size={16} />
                {file.name}
              </div>
            )}

            <div style={styles.buttonGroup}>
              <button
                style={styles.button("primary")}
                onClick={processExcel}
                disabled={!file || processing}
              >
                {processing ? t.processing : t.processExcel}
                <Calculator size={18} />
              </button>

              {processedData && (
                <button
                  style={styles.button("success")}
                  onClick={downloadExcel}
                >
                  {t.downloadResults}
                  <Download size={18} />
                </button>
              )}
            </div>

            {bulkError && (
              <div style={styles.alertBox("error")}>
                <AlertCircle size={20} />
                {bulkError}
              </div>
            )}

            {success && (
              <div style={styles.alertBox("success")}>
                <CheckCircle size={20} />
                {t.successfullyProcessed} {processedData?.length} {t.employeeRecords}
              </div>
            )}
          </div>

          {processedData && processedData.length > 0 && (
            <div style={styles.card} 
            //onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}
            >
              <h3 style={{
                fontSize: screenSize.isMobile ? "18px" : "20px",
                fontWeight: "700",
                color: currentTheme.colors.textPrimary,
                marginBottom: "16px",
                textAlign: isRTL ? 'right' : 'left',
              }}>
                {t.resultsPreview} ({processedData.length} {t.records})
              </h3>
              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>{t.employeeName}</th>
                      <th style={styles.th}>{t.insuranceSalary}</th>
                      <th style={styles.th}>{t.netSalary}</th>
                      <th style={styles.th}>{t.grossSalary}</th>
                      <th style={styles.th}>{t.monthlyTax}</th>
                      <th style={styles.th}>{t.yearlyTax}</th>
                      <th style={styles.th}>{t.martyrsFund}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedData.slice(0, 10).map((row, index) => (
                      <tr key={index}>
                        <td style={styles.td}>{row[t.employeeName]}</td>
                        <td style={styles.td}>{row[t.insuranceSalary]}</td>
                        <td style={styles.td}>{row[t.netSalary]}</td>
                        <td style={styles.td}>{row[t.grossSalary]}</td>
                        <td style={styles.td}>{row[t.monthlyTax]}</td>
                        <td style={styles.td}>{row[t.yearlyTax]}</td>
                        <td style={styles.td}>{row[t.martyrsFund]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {processedData.length > 10 && (
                  <div style={{
                    marginTop: "16px",
                    textAlign: "center",
                    color: currentTheme.colors.textMuted,
                    fontSize: screenSize.isMobile ? "12px" : "14px"
                  }}>
                    {t.showingFirst} {processedData.length} {t.recordsDownload}
                  </div>
                )}
              </div>
            </div>
          )}
        </React.Fragment>
      )}

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }

          .upload-area:hover {
            background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} !important;
            border-color: #2e7cba !important;
            transform: scale(1.01);
          }

          input:focus {
            border-color: #2e7cba !important;
            box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
            transform: scale(1.01);
          }

          button:not(:disabled):hover {
            transform: translateY(-2px) scale(1.02);
          }

          button:active {
            transform: translateY(0) scale(0.98);
          }

          ::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          
          ::-webkit-scrollbar-track {
            background: ${currentTheme.colors.scrollbar?.track || (theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)')};
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: linear-gradient(135deg, #2e7cba, #8b5cf6);
            border-radius: 10px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: linear-gradient(135deg, #2563eb, #7c3aed);
          }

          button:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          @media (max-width: 768px) {
            .mode-switch, .calc-mode-toggle {
              grid-template-columns: 1fr !important;
            }
          }

          * {
            box-sizing: border-box;
          }

          *:focus-visible {
            outline: 2px solid #2e7cba;
            outline-offset: 2px;
          }

          ::selection {
            background: rgba(59,130,246,0.2);
            color: ${currentTheme.colors.textPrimary};
          }
        `}
      </style>
    </div>
  );
};

export default UnifiedSalaryCalculator;


